'use client'

import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { ArticleSidebarForm } from '@/app/[locale]/blog/_components/ArticleSidebarForm'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import { ARTICLE_SIDEBAR_TEST_IDS } from '@/lib/test-ids'
import { getColorVariant } from '@/utils/colorMapping'

export const ArticleSidebar = ({
	sidebar,
}: {
	sidebar?: Data.Component<'blog.article-sidebar'> | null
}) => {
	const t = useTranslations('ArticleSidebar')

	const variant = sidebar?.variant ?? 'default'
	const bgClassName =
		variant === 'blank'
			? 'bg-white'
			: getColorVariant(sidebar?.backgroundColor?.color, 'bg', 'peach-200')

	return (
		<div data-test-id={ARTICLE_SIDEBAR_TEST_IDS.COMPONENT}>
			{sidebar?.image && (
				<div
					className={cn('w-full overflow-hidden lg:rounded-b-none lg:rounded-t-xl', bgClassName)}
				>
					<div className="relative w-full lg:h-37.5 h-45 ">
						<BasicImage
							component={sidebar?.image}
							className="lg:object-cover object-contain"
							fill
						/>
					</div>
				</div>
			)}
			<div
				className={cn(
					'rounded-b-xl  gap-4 flex flex-col',
					bgClassName,
					variant === 'default' && 'p-5',
					sidebar?.image ? 'rounded-t-none' : 'rounded-t-xl',
				)}
			>
				<div className={cn('flex flex-col', variant === 'blank' ? 'gap-4' : 'gap-3')}>
					<div className="flex flex-col gap-1">
						{sidebar?.label && <Paragraph fontSize="bodyRegularMedium">{sidebar.label}</Paragraph>}
						<Heading
							tag="h2"
							fontSize={variant === 'blank' ? 'serifMedium' : 'headingSmall'}
							className={cn(variant === 'blank' && '!text-5xl/14')}
						>
							{sidebar?.title ?? t('title')}
						</Heading>
					</div>
					{sidebar?.description && (
						<Paragraph fontSize={variant === 'blank' ? 'bodyRegularLarge' : 'bodyRegularMedium'}>
							{sidebar?.description}
						</Paragraph>
					)}
				</div>
				<ArticleSidebarForm sidebar={sidebar} />
			</div>
		</div>
	)
}
